import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import { createSelector } from '@reduxjs/toolkit'
import { useAppDispatch } from 'store'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'

import _ from 'lodash'

import Icon from '@mdi/react'
import { mdiArrowCollapseRight, mdiArrowCollapseLeft, mdiArrowCollapseUp } from '@mdi/js'

import Button from 'devextreme-react/button'
import Tooltip from 'devextreme-react/tooltip'
import DropDownButton from 'devextreme-react/drop-down-button'
import { DrawerState } from 'helpers/drawer-state-enum'
import LoadingIndicator from 'devextreme-react/load-indicator'
import ContextMenu, { ContextMenuTypes } from 'devextreme-react/context-menu'
import { Template } from 'devextreme-react/core/template'
import { ContextMenuLoadingComponent, ContextMenuItemComponent } from 'components/grid-context-menu/grid-context-menu'

// import { useActionPanel } from 'contexts/action-panel-context'
import { IxConfirmBtn, IxInfoBtn } from 'components'
import { RootState } from 'slices'
import { setActionPanelState, PanelState } from 'slices/ui'
import { setActions } from 'slices/action-panel'
import { getIconClass } from 'helpers/get-icon-class'

import './action-panel.scss'

const onShowing = (e: any) => {
    const className = 'actionDropdown--overlay'
    e.component.content().classList.add(className)
    e.component.content().setAttribute('data-testId', 'actionDropdown')
}

const prepContextMenuItems = (menuItems: any[]) => {
    const prepSubItem = (cb: Function) => {
        return (i: any) => ({
            text: i.name,
            onItemClick: () => cb && cb({ itemData: i }),
            ...i,
        })
    }

    const prepItem =(a: any, i: number) => {
        if (a.widget === 'loading') {
            return { text: '', template: 'loading', beginGroup: true }
        } else {
            let _items: any = undefined
            if (a.widget === 'dropdown') {
                _items = a.options.items.map(prepSubItem(a.options.onItemClick))
            } else if (a.widget === 'contextmenu') {
                _items = a.options.items.map(prepItem)
            }
            return {
                text: a.name,
                icon: a.options.icon,
                onItemClick: a.options.onClick,
                beginGroup: i === 0 || a.beginGroup,
                disabled: a.widget === 'disabledbtn',
                hint: a.hint,
                items: _items,
            }
        }
    }

    const items = menuItems.map(prepItem)

    return items
}

const onItemClick = (e: ContextMenuTypes.ItemClickEvent) => {
    e.itemData?.onItemClick && e.itemData.onItemClick()
}

const RenderAction = (action: any) => {
    const ctxRef = useRef<any>(null)
    switch(action.widget) {
        case 'button': {
            return (
                <div id={`actionBtn-${action.id}`} key={`actionBtn-${action.id}`} className={ `actionPanel--item ${action.dirty ? 'actionPanel--item--dirty' : ''}` }>
                    <Button {...action.options} name={action.name} />
                    <Tooltip target={`#actionBtn-${action.id}`} position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{action.name}</div>
                    </Tooltip>
                </div>
            )
        }
        case 'dropdown': {
            return (
                <div id={`actionDropdown-${action.id}`} key={`actionDropdown-${action.id}`} className={ `actionPanel--item ${action.dirty ? 'actionPanel--item--dirty' : ''}` }>
                    <DropDownButton
                        {...action.options}
                        name={action.name}
                        displayExpr='name'
                        dropDownOptions={{width: 'auto', onShowing, wrapperAttr: { class: 'actionPanel-dropdown-wrapper' }}}
                        itemComponent={ DropDownButtonItemComponent }
                    />
                    <Tooltip target={`#actionDropdown-${action.id}`} position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{action.name}</div>
                    </Tooltip>
                </div>
            )
        }
        case 'contextmenu': {
            return (
                <div id={`actionCtxBtn-${action.id}`} key={`actionDropdown-${action.id}`} className={ `actionPanel--item ${action.dirty ? 'actionPanel--item--dirty' : ''}` }>
                    <Button
                        {...action.options}
                        id={`actionDropdown-${action.id}-btn`}
                        name={action.name}
                        onClick={ () => { ctxRef.current?.instance().show() } }
                        className='dx-ctxmenubutton'
                    >
                        <i className={ getIconClass(action.options.icon) } />
                        <i className='dx-icon dx-icon-spindown dx-icon-right' />
                    </Button>
                    <ContextMenu
                        ref={ctxRef}
                        target={`#actionDropdown-${action.id}-btn`}
                        showEvent=''
                        items={ prepContextMenuItems(action.options.items) }
                        itemComponent={ ContextMenuItemComponent }
                        onItemClick={ onItemClick }
                        position={ { my: 'top left', at: 'bottom left' } as any }
                        cssClass='actionPanel--context-menu'
                        height='auto'
                    >
                        <Template name='loading' render={ ContextMenuLoadingComponent } />
                    </ContextMenu>
                    <Tooltip target={`#actionCtxBtn-${action.id}`} position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{action.name}</div>
                    </Tooltip>
                </div>
            )
        }
        case 'confirmbtn': {
            return (
                <div id={`actionBtn-${action.id}`} key={`actionBtn-${action.id}`} className={ `actionPanel--item ${action.dirty ? 'actionPanel--item--dirty' : ''}` }>
                    <IxConfirmBtn {...action.options} />
                    <Tooltip target={`#actionBtn-${action.id}`} position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{action.name}</div>
                    </Tooltip>
                </div>
            )
        }
        case 'infobtn': {
            return (
                <div id={`actionBtn-${action.id}`} key={`actionBtn-${action.id}`} className={ `actionPanel--item ${action.dirty ? 'actionPanel--item--dirty' : ''}` }>
                    <IxInfoBtn {...action.options} />
                    <Tooltip target={`#actionBtn-${action.id}`} position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{action.name}</div>
                    </Tooltip>
                </div>
            )
        }
        case 'disabledbtn': {
            return (
                <div id={`actionBtn-${action.id}`} key={`actionBtn-${action.id}`} className={ `actionPanel--item ${action.dirty ? 'actionPanel--item--dirty' : ''}` }>
                    <IxInfoBtn btnOptions={{ ...action.options, elementAttr: { class: 'disabled-action' } }} title='Warning' description={ action.hint } />
                    <Tooltip target={`#actionBtn-${action.id}`} position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{action.name}: {action.hint}</div>
                    </Tooltip>
                </div>
            )
        }
        case 'delimiter': {
            return (
                <div id={`actionBtn-${action.id}`} key={`actionBtn-${action.id}`} className='actionPanel--item actionPanel--delimiter'>
                </div>
            )
        }
        case 'loading': {
            return (
                <div id={`actionBtn-${action.id}`} key={`actionBtn-${action.id}`} className='actionPanel--item'>
                    <Button disabled={true} icon='spin' >
                        <LoadingIndicator width={18} height={18} visible={ true } />
                    </Button>
                </div>
            )
        }
    }
}

const actionPanelSelector = createSelector(
    [
        (state: RootState) => state.ui.drawerState,
        (state: RootState) => state.ui.tabs,
        (state: RootState) => state.ui.actionPanelState,
        (state: RootState) => state.ui.tabsPanelState,
        (state: RootState) => state.actionPanel.actions,
    ],
    (drawerState, tabs, actionPanelState, tabsPanelState, actions) => ({
        drawerState,
        tabs,
        actionPanelState,
        tabsPanelState,
        actions,
    })
)

const DropDownButtonItemComponent = ({ data: itemData }: any) => {
    const iconClass = itemData.icon && 'dx-list-item-icon' + (itemData.icon.split(' ').length === 1 ? `dx-icon dx-icon-${itemData.icon}` : itemData.icon)
    const iconStyle = itemData.iconColor ? { color: itemData.iconColor } : {}

    return (
        <div>
            { itemData.icon && (
                <div className='dx-list-item-icon-container'>
                    <i className={ iconClass } style={ iconStyle } />
                </div>
            ) }
            {itemData.name}
        </div>
    )
}

export const ActionPanel = () => {
    const location = useLocation()
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const { drawerState, tabs, actionPanelState, tabsPanelState, actions } = useSelector(actionPanelSelector)

    const setStateCallback = useCallback((state: PanelState) => (() =>  dispatch(setActionPanelState(state))), [dispatch])

    const sortedActions = useMemo<any[]>(() => _.sortBy(actions, 'id'), [actions])

    const getPanelStateClass = useCallback((state: PanelState, tabsPanelState: PanelState) => {
        let className = `actionPanel-${state}`

        // if tabs and action panel are collapsed to the same side, make action panel go to the inside of the page
        if (tabs.length) {
            if (state !== 'top' && state === tabsPanelState) { className = `${className} actionPanel-offset` }
        }

        return className
    }, [tabs])

    const beforeActions = useMemo(() => {
        const actions =  sortedActions
        .filter(action => (action.location !== 'center' && action.location !== 'after'))
        .map((action, i) => <RenderAction key={i} { ...action } />)

        if (actionPanelState !== 'top') {
            actions.unshift(
                <div id='actionPanelToggleBtn' key='actionPanelToggleBtn' className='actionPanel--item'>
                    <Button icon={actionPanelState === 'right' ? 'chevrondoubleleft' : 'chevrondoubleright' } onClick={setStateCallback('top')}>
                        <Icon path={mdiArrowCollapseUp} size='16px' style={{ margin: 1 }} />
                    </Button>
                    <Tooltip target='#actionPanelToggleBtn' position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                        <div>{t('global:action.expand')}</div>
                    </Tooltip>
                </div>
            )
        }

        return actions
    },
    [sortedActions, actionPanelState, setStateCallback, t])

    const centerActions = useMemo(() => (
        sortedActions
        .filter(action => action.location === 'center')
        .map((action, i) => <RenderAction key={i} { ...action } />)
    ), [sortedActions])

    const afterActions = useMemo(() => {
        const actions = sortedActions
        .filter(action => action.location === 'after')
        .map((action, i) => <RenderAction key={i} { ...action } />)

        if (actionPanelState === 'top') {
            actions.push(
                    <div id='actionPanelToggleBtnLeft' key='actionPanelToggleBtnLeft' className='actionPanel--item'>
                        <Button icon='chevrondoubleleft' onClick={setStateCallback('left')}>
                            <Icon path={mdiArrowCollapseLeft} size='16px' style={{ margin: 1 }} />
                        </Button>
                        <Tooltip target='#actionPanelToggleBtnLeft' position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                            <div>{t('global:action.collapse')}</div>
                        </Tooltip>
                    </div>
            )
            actions.push(
                    <div id='actionPanelToggleBtnRight' key='actionPanelToggleBtnRight' className='actionPanel--item'>
                        <Button icon='chevrondoubleright' onClick={setStateCallback('right')}>
                            <Icon path={mdiArrowCollapseRight} size='16px' style={{ margin: 1 }} />
                        </Button>
                        <Tooltip target='#actionPanelToggleBtnRight' position='bottom' showEvent={{ name: 'dxhoverstart', delay: 200 }} hideEvent='dxhoverend'>
                            <div>{t('global:action.collapse')}</div>
                        </Tooltip>
                    </div>
            )
        }

        return actions
    }, [sortedActions, actionPanelState, setStateCallback, t])

    useEffect(() => {
        dispatch(setActions([]))
    }, [dispatch, location])

    if (actions.length === 0) return null

    return (
        <div
            className={`actionPanel ${getPanelStateClass(actionPanelState, tabsPanelState)}`}
            style={(actionPanelState !== 'top' && { top: tabsPanelState === 'top' ? '5px' : '15px' }) || {}}
        >
            <div className='actionPanel--before' style={{marginLeft: drawerState === DrawerState.Closed ? '40px' : ''}}>
                { beforeActions }
            </div>

            <div className='actionPanel--center'>
                { centerActions }
            </div>

            <div className='actionPanel--after'>
                { afterActions }
            </div>
        </div>
    )
}

export const LocalActionPanel = ({
    actions,
}: any) => {
    const sortedActions = useMemo<any[]>(() => _.sortBy(actions, 'id'), [actions])

    const renderedActions = useMemo(() => {
        const actions =  sortedActions
        .filter(action => (action.location !== 'center' && action.location !== 'after'))
        .map((action, i) => <RenderAction key={i} { ...action } id={ action.id + 1000 } />)

        return actions
    },
    [sortedActions])

    return (
        <div className='actionPanel actionPanel-local'>
            <div className='actionPanel--before'>
                { renderedActions }
            </div>
        </div>
    )
}
